import React, { useEffect, useState } from 'react';
import { getMarketAverageVolume, getTransactionVolume } from '../../service/service-gateway';
import { HelixIcon } from '@lwt-helix/helix-icon';
import { chevron_small_right, circle_info } from '@lwt-helix/helix-icon/outlined';
import { useCommonStore, useSearchStore } from '../../store/store';
import Loader from '@lwt-helix/loader';
import { getCurrencyFormat } from '../../agent-production/helpers/dataFormatters';
import { ChartLegend } from './ChartLegend';
import { ChartLegendLabel } from './ChartLegendLabel';
import { ChartLegendValue } from './ChartLegendValue';
import { agentProductionTerms, moduleNames } from '../../constants';
import { terms, viewTypes } from '../constants';
import TransactionVolumeChart from './TransactionVolumeChart';
import { format, parseISO } from 'date-fns';
import { getProductURL } from '../../common/helpers/nav';
import { useHistory } from 'react-router-dom';
import { useAuthStore } from '../../store/auth/store';

const TransactionVolume = () => {
    const [isLoadingData, setIsLoadingData] = useState(false);
    const [transactionVolumeData, setTransactionVolumeData] = useState(null);
    const [marketAverageVolumeData, setMarketAverageVolumeData] = useState(null);
    const [chartData, setChartData] = useState({ categories: [], transactionVolumes: [], marketAverages: [] });
    const history = useHistory();

    const {
        selectedMls,
        selectedOffice,
        viewTypeId,
    } = useSearchStore(state => ({
        selectedMls: state[state.selectedModule]?.selectedMls,
        selectedOffice: state[state.selectedModule]?.selectedOffice,
        viewTypeId: state[state.selectedModule]?.viewTypeId
    }));
    const {
        getAssociatedBrokerageId,
        userProducts,
    } = useCommonStore(state => ({
        getAssociatedBrokerageId: state.getAssociatedBrokerageId,
        userProducts: state.privileges?.userProducts,
    }));
    const isLegacyUser = useAuthStore((state) => state.isLegacyUser);
    const hasProficiencyMetricsAccess = !!(userProducts?.includes(moduleNames.proficiencyMetrics) || isLegacyUser);

    const selectedBrokerId = getAssociatedBrokerageId();

    useEffect(() => {

        const timeoutId = setTimeout(() => {
            const fetchData = async () => {
                const selectedMlsId = selectedMls?.mlsId.toString();
                const selectedOfficeId = selectedOffice?.mlsOfficeId?.officeId?.toString();
                if (!selectedMlsId) return;
                setIsLoadingData(true);
                try {
                    const officeIdParam = viewTypeId === viewTypes.byOffice.id ? selectedOfficeId : null;
                    const brokerIdParam = viewTypeId === viewTypes.byBrokerage.id ? selectedBrokerId : null;

                    const [transactionResponse, marketAverageResponse] = await Promise.all([
                        getTransactionVolume(selectedMlsId, officeIdParam, brokerIdParam),
                        getMarketAverageVolume(selectedMlsId, officeIdParam, brokerIdParam)
                    ]);

                    const transactionVolume = (transactionResponse?.transaction_volume || []).reduce((sum, month) => sum + month.total_volume, 0);
                    setTransactionVolumeData(transactionVolume);

                    const monthLabels = transactionResponse?.transaction_volume?.map(item => format(parseISO(item.startDate), 'MMM').toUpperCase());
                    const transactionVolumes = transactionResponse?.transaction_volume?.map(item => item.total_volume || 0);

                    setMarketAverageVolumeData(marketAverageResponse?.total_market_average);

                    const monthlyMarketAverage = marketAverageResponse?.monthly_market_average || [];
                    const marketAverages = monthlyMarketAverage.map(month => month.average || 0);
                    setChartData({
                        categories: monthLabels,
                        transactionVolumes,
                        marketAverages,
                    });
                } catch (err) {
                    console.error(err);
                    setTransactionVolumeData(null);
                    setMarketAverageVolumeData(null);
                } finally {
                    setIsLoadingData(false);
                }
            };
            fetchData();
        }, 10);
        return () => clearTimeout(timeoutId);

    }, [selectedMls?.mlsId, selectedOffice?.mlsOfficeId?.officeId, viewTypeId, selectedBrokerId]);

    const chartLegend = <ChartLegend>
        <div className="d-flex gap-4">
            <div className='d-flex flex-column mr-4'>
                <ChartLegendLabel variant="dark" shape="square">
                    {viewTypeId === 'byOffice' ? (selectedOffice?.name || terms.selectedOffice) : 'Your Brokerage'}
                </ChartLegendLabel>
                <ChartLegendValue>
                    {getCurrencyFormat(transactionVolumeData)}
                </ChartLegendValue>
            </div>
            <div className="d-flex flex-column">
                <ChartLegendLabel variant="blue" shape="line">
                    {terms.marketAverage}
                </ChartLegendLabel>
                <ChartLegendValue>
                    {getCurrencyFormat(marketAverageVolumeData)}
                </ChartLegendValue>

            </div>
        </div>
    </ChartLegend >;

    return (
        <div className="content d-flex flex-column h-100 p-4 dashboard-card">
            <div className="d-flex justify-content-between align-items-start mb-4">
                <div className='d-flex align-items-center'>
                    <h2 className="helix-legend-value mb-1 mr-2">{terms.transactionVolume}</h2>
                    <HelixIcon icon={circle_info} className="helix-icon--small helix-text-gray-400 mr-2" />
                    <p className="helix-info-text mb-0">{terms.allMlsAndPropertyTypes}</p>
                </div>
                {hasProficiencyMetricsAccess && (
                    <button
                        className="helix-body--strong dashboard-nav-btn helix-brand-color"
                        onClick={() =>
                            history.push(getProductURL({product: moduleNames.proficiencyMetrics, userProducts, isLegacyUser}))
                        }
                    >
                        {agentProductionTerms.proficiencyMetrics}
                        <HelixIcon
                            icon={chevron_small_right}
                            className="ml-1"
                        />
                    </button>
                )}
            </div>
            {isLoadingData ? (
                <div className="d-flex justify-content-center align-items-center pb-4">
                    <Loader />
                </div>
            ) : (
                chartLegend
            )}
            {isLoadingData ? null : (
                <TransactionVolumeChart
                    chartData={chartData}
                    selectedOffice={selectedOffice}
                    viewTypeId={viewTypeId}
                />
            )}
        </div>
    );
};

export default TransactionVolume;
